/* eslint-disable max-len */
import { DownloadXls, WeatherArrowDropDown, WeatherStationCloseIcon } from '@/resource/IconComponentSets';
import ColorMaker from '@/util/colorMaker';
import { downloadWithStation } from '@/util/Request';
import { IWeatherChardDataReqModel } from '@/util/Request/requestType';
import { RootState } from '@reduxjs/toolkit';
import { Dropdown, Menu } from 'antd';
import ReactEcharts from 'echarts-for-react';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IChartLineProps, IChartOptionsProps } from '../../../SoilChartComponent/SoilChartComponent';
import './index.less';
import { updateCurrentShowDetailKey } from '@/redux/reducer/common';

interface IProps {
    className?: string,
    lat?: string,
    lng?: string,
    children?: React.ReactNode,
    key?: string
    color?: string
    stationId: string
    chartVisibleCallBack: (chartVisible: boolean, key: string) => void
}

interface IChart {
    [unknown: string]: {
        chartData?: IChartOptionsProps
        stationData?: IChartLineProps[]
    }
}

interface IItems {
    label: React.ReactNode
    key: string
}

const mapObj = {
    E_AIR_TEMPERATURE: 'Temperature (°C)',
    E_ATM_PRESSURE: 'Pressure (hPa)',
    E_DEWPOINT: 'Dewpoint Temperature (°C)',
    E_ELEC_RESISTANCE: 'Electrical resistance (ohm)',
    E_LEAF_SENSOR_CONDUCTIVITY: 'Leaf Sensor Conductivity (mV)',
    E_LEAF_TEMPERATURE: 'Leaf Temperature (°C)',
    E_LEAF_WETNESS: 'Leaf wetness probability (%)',
    E_PAR_DN: 'Photosynthetic active radiation (W/m²)',
    E_PH: 'pH',
    E_RAINFALL: 'Precipitation Total (mm)',
    E_REF_ET: 'Evapotranspiration (mm)',
    E_RELATIVE_HUMIDITY: 'Relative Humidity (%)',
    E_SHRT_WAVE_RAD_DN: 'Shortwave Radiation (W/m²)',
    E_SNOW_DEPTH: 'Snow Depth (m)',
    E_SOIL_TEMPERATURE: 'Soil Temperature (°C)',
    E_SOIL_WATER_CONTENT: 'Soil Moisture (m³/m³)',
    E_SOIL_WATER_POTENTIAL: 'Soil Moisture Available To Plant (fraction)',
    E_SOIL_WATER_TENSION: 'Soil tension (kPa)',
    E_V_PRESS_DEFICIT: 'Vapor Pressure Deficit (hPa)',
    E_WATER_DEPTH: 'Water Depth (m)',
    E_WATER_TEMPERATURE: 'Water Temperature (°C)',
    E_WB_TEMPERATURE: 'Wetbulb Temperature (°C)',
    E_WIND_DIRECTION: 'Wind Direction (°)',
    E_WIND_GUST: 'Wind Gust (km/h)',
    E_WIND_HEADING: 'Wind Direction (°)',
    E_WIND_SPEED: 'Wind Speed (km/h)',
    M_WATER_PRESSURE: 'Water Pressure (hPa)',
    SOLAR_IRRADIATION: 'Shortwave Radiation (W/m²)',
    SUNSHINE_HOURS: 'Sunshine Duration (min)',
    VAPOR_PRESSURE: 'Vapor Pressure (hPa)',
    E_WATERMARK_30CM: 'Watermark 30cm (kPa)',
    E_WATERMARK_60CM: 'Watermark 60cm (kPa)',
    E_SOIL_MOISTURE_10CM: 'Soil Moisture (%)/10 cm',
    E_SOIL_MOISTURE_20CM: 'Soil Moisture (%)/20 cm',
    E_SOIL_MOISTURE_30CM: 'Soil Moisture (%)/30 cm',
    E_SOIL_MOISTURE_40CM: 'Soil Moisture (%)/40 cm',
    E_SOIL_MOISTURE_50CM: 'Soil Moisture (%)/50 cm',
    E_SOIL_MOISTURE_60CM: 'Soil Moisture (%)/60 cm',
    E_SOIL_MOISTURE_TENSION_L1: 'Soil Moisture Tension (kPa)/L1',
    E_SOIL_MOISTURE_TENSION_L2: 'Soil Moisture Tension (kPa)/L2',
    E_SOIL_MOISTURE_TENSION_L3: 'Soil Moisture Tension (kPa)/L3',
    E_SOIL_MOISTURE_TENSION_L4: 'Soil Moisture Tension (kPa)/L4',
    E_SOIL_WATER_VOLUMETRIC_CONTENT_10CM: 'Volumetric Ionic Content (VIC)/10 cm',
    E_SOIL_WATER_VOLUMETRIC_CONTENT_20CM: 'Volumetric Ionic Content (VIC)/20 cm',
    E_SOIL_WATER_VOLUMETRIC_CONTENT_30CM: 'Volumetric Ionic Content (VIC)/30 cm',
    E_SOIL_WATER_VOLUMETRIC_CONTENT_40CM: 'Volumetric Ionic Content (VIC)/40 cm',
    E_SOIL_WATER_VOLUMETRIC_CONTENT_50CM: 'Volumetric Ionic Content (VIC)/50 cm',
    E_SOIL_WATER_VOLUMETRIC_CONTENT_60CM: 'Volumetric Ionic Content (VIC)/60 cm',
    E_SOIL_TEMPERATURE_2CM: 'Soil Temperature (°C)/2 cm',
    E_SOIL_TEMPERATURE_5CM: 'Soil Temperature (°C)/5 cm',
    E_SOIL_TEMPERATURE_10CM: 'Soil Temperature (°C)/10 cm',
    E_SOIL_TEMPERATURE_20CM: 'Soil Temperature (°C)/20 cm',
    E_SOIL_TEMPERATURE_30CM: 'Soil Temperature (°C)/30 cm',
    E_SOIL_TEMPERATURE_40CM: 'Soil Temperature (°C)/40 cm',
    E_SOIL_TEMPERATURE_50CM: 'Soil Temperature (°C)/50 cm',
    E_SOIL_TEMPERATURE_60CM: 'Soil Temperature (°C)/60 cm',
    E_SOIL_TEMPERATURE_100CM: 'Soil Temperature (°C)/100 cm',
    E_AIR_TEMPERATURE_NODE1: 'Temperature (°C)/node1',
    E_CANOPY_TEMPERATURE: 'Canopy Temperature (°C)'
};


const WeatherSunnyNormalSvg = (props: IProps) => {
    const {
        color,
        stationId,
        key,
        lat,
        lng,
        chartVisibleCallBack
    } = props;

    const echartMarkerRef = useRef(null);

    const [cardChartData, setCardChartData] = useState<IChart>({});
    const [render, setRender] = useState(false);
    const [currenTab, setCurrenTab] = useState<string>();
    const [tabString, setTabString] = useState<string[]>([]);
    const [items, setItems] = useState<IItems[]>([]);
    const [chartVisible, setChartVisible] = useState(false);
    const [yPoint, setYPoint] = useState<string>('-260');
    const [xPoint, setXPoint] = useState<string>('-20');
    const [chartVisibleData, setChartVisibleData] = useState<IChartOptionsProps>();
    const [currentZIndex, setCurrentZIndex] = useState(100);


    const transportToTargetFormat = (dateStr: string): string => {
        const monthMap = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'July', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const year = dateStr.substring(2, 4);
        const month = dateStr.substring(4, 6);
        const day = dateStr.substring(6, 8);
        const hour = dateStr.match(/(?<=T)[\d]*/);
        let targetStr;
        if (hour && hour[0]) {
            targetStr = `${hour}:00 ${day} ${monthMap[parseInt(month, 10) - 1]} ${year}`;
        } else {
            targetStr = `${day} ${monthMap[parseInt(month, 10) - 1]} ${year}`;
        }

        return targetStr;
    };

    const dispatch = useDispatch();

    const chartData = useSelector((state: RootState) => state.weatherStation.chartData);
    const variableMapToUm = useSelector((state: RootState) => state.weatherStation.variableMapToUm);
    const requestOptions = useSelector((state: RootState) => state.weatherStation.requestOptions);
    const currentShowDetailKey = useSelector((state: RootState) => state.commonData.currentShowDetailKey);

    useEffect(() => {
        if (variableMapToUm !== null) {
            setChartVisible(false);
            if (stationId && chartData) {
                const categorySet = new Set<string>();
                const stationChartData = chartData.filter((chartDataItem) => chartDataItem.stationID === stationId);

                const chartDocument: IChart = {};
                const colorMaker = new ColorMaker({ totalColorsNumber: 10 });

                stationChartData.forEach((item, index) => {
                    if (item.hasData && item.timeSequence.length > 0) {
                        item.timeSequence.forEach((time: string) => {
                            categorySet.add(time);
                        });
                    }

                    const values = item.values === null ? [] : item.values;
                    const valueArr = values.map((value) => parseFloat(value));
                    const color = colorMaker.getColor((index + 1) * 0.2);
                    const data: IChartLineProps = {
                        name: `${item.supplier}-${item.stationID}`,
                        type: 'line',
                        // stack: 'Total',
                        data: valueArr,
                        celisusValues: [...(valueArr && [])],
                        fahrenheitValues: [],
                        kmValues: [],
                        msValues: [],
                        stationId: item.stationID,
                        selected: true,
                        supplier: item.supplier,
                        longitude: item.longitude,
                        latitude: item.latitude,
                        color,
                        variableName: mapObj[item.variable] || 'Unknow',
                        isTemperature: false,
                        isWindSpeed: false,
                        unit: variableMapToUm[item.variable]
                    };
                    const propertyStr = `${item.supplier}-${item.variable}-${item.frequency}`;
                    if (chartDocument[propertyStr] && chartDocument[propertyStr]['stationData']) {
                        chartDocument[propertyStr]['stationData'].push(data);
                    } else {
                        chartDocument[propertyStr] = {
                            ...chartDocument,
                            stationData: [data]
                        };
                    }
                });

                const categoryArr: string[] = Array.from(categorySet);
                const transportDateArr = categoryArr.map((date) => transportToTargetFormat(date));
                const keys = Object.keys(chartDocument);
                for (const key of keys) {
                    const data = chartDocument[key]['stationData'];
                    const title = data[0].unit;
                    const constructChartOptionData: IChartOptionsProps = {
                        title: {
                            text: title,
                            textStyle: {
                                fontWeight: 'normal',
                                fontSize: 14
                            }
                        },
                        tooltip: {
                            trigger: 'axis'
                        },
                        grid: {
                            left: '3%',
                            right: '14%',
                            bottom: '1%',
                            top: '12%',
                            containLabel: true
                        },
                        xAxis: {
                            // value | category | time | log
                            type: 'category',
                            boundaryGap: false,
                            data: transportDateArr,
                            axisLabel: {
                                rotate: -40
                            }
                        },
                        yAxis: {
                            type: 'value'
                        },
                        series: data,
                        animation: true
                    };
                    chartDocument[key]['chartData'] = constructChartOptionData;
                }

                const currentTabKeys = Object.keys(chartDocument);

                setTabString(currentTabKeys);
                setCurrenTab(currentTabKeys[0]);
                setCardChartData(chartDocument);
            }
        }
    }, [chartData, stationId, variableMapToUm]);

    const hoverCallBack = async (isEnter) => {
        if (cardChartData[currenTab] && isEnter) {
            setRender(false);
            dispatch(updateCurrentShowDetailKey(stationId));
            setTimeout(() => setRender(true), 100);
        } else {
            dispatch(updateCurrentShowDetailKey(''));
        }
    };

    useEffect(() => {
        if (currentShowDetailKey === stationId) {
            setChartVisible(true);
        } else {
            setChartVisible(false);
        }
    }, [currentShowDetailKey]);

    useEffect(() => {
        if (tabString.length > 0) {
            const items = tabString.map((tabName) => {
                const nameArr = tabName.split('-');
                return {
                    label: <span className='tab-property'>{nameArr[0]}-{mapObj[nameArr[1]]}-{nameArr[2]}</span>,
                    key: tabName,
                    onClick: () => setCurrenTab(tabName)
                };
            });
            setItems(items);
        }
    }, [tabString]);

    useEffect(() => {
        if (chartVisible) {
            const targetDiv = document.getElementById(`visible-div-marker-${stationId}-inner`);
            const top = targetDiv.getBoundingClientRect().top;
            const distanceToLeft = targetDiv.getBoundingClientRect().right;
            const mapDiv = document.getElementById('app-component');
            const screenWidth = mapDiv.getBoundingClientRect().width;

            const right = screenWidth - distanceToLeft;
            if (top > 400) {
                setYPoint(`${-(300 + 95)}px`);
            }

            if (top < 400) {
                setYPoint(`${-top + 62}px`);
            }

            if (right > (401)) {
                setXPoint(`${-42 / 2}px`);
            }

            if (right < (401)) {
                setXPoint(`${380 + 42 / 2}px`);
            }
        }
    }, [chartVisible, stationId]);

    const defineTheChartInstance = (param) => {
        echartMarkerRef.current = param;
    };

    const constructNullChartOptionData: IChartOptionsProps = {
        title: {
            text: ''
        },
        tooltip: {
            trigger: 'axis'
        },
        grid: {
            left: '3%',
            right: '10%',
            bottom: '0%',
            containLabel: true
        },
        xAxis: {
            // value | category | time | log
            type: 'category',
            boundaryGap: false,
            data: []
        },
        yAxis: {
            type: 'value'
        },
        series: [],
        animation: false
    };

    useEffect(() => {
        if (render && cardChartData) {
            setChartVisibleData({ ...cardChartData[currenTab]?.chartData });
        }
    }, [render, cardChartData, currenTab]);

    useEffect(() => {
        const targetDiv = document.getElementById(`visible-div-marker-${stationId}-inner`);
        targetDiv.style.zIndex = `${currentZIndex}`;
    }, [currentZIndex]);

    useEffect(() => {
        chartVisibleCallBack && chartVisibleCallBack(chartVisible, key);
    }, [chartVisible]);

    const downloadCurrentChart = () => {
        const base64 = echartMarkerRef.current._api.getDataURL({ type: 'png', pixelRatio: 0.5, backgroundColor: '#fff' });
        const blob = transformBase64ToBlob(base64);
        const link = document.createElement('a');
        const propertyArr = currenTab.split('-');

        link.download = `${propertyArr[0]} ${mapObj[propertyArr[1]]}.png`;
        link.style.display = 'none';
        link.href = URL.createObjectURL(blob);
        document.body.append(link);
        link.click();
        URL.revokeObjectURL(link.href);
        document.body.removeChild(link);
    };

    const transformBase64ToBlob = (base64: string) => {
        const picStr = base64.split(';base64,');
        const contentType = picStr[0].split(':')[1];
        const buffer = atob(picStr[1]);

        let bufferLength = buffer.length;
        const u8Arr = new Uint8Array(bufferLength);
        while (bufferLength--) {
            u8Arr[bufferLength] = buffer.charCodeAt(bufferLength);
        }
        const blob = new Blob([u8Arr], { type: contentType });
        return blob;
    };

    const downloadStationData = async () => {
        const stationChartData = chartData.filter((chartDataItem) => chartDataItem.stationID === stationId);
        if (stationChartData.length < 0) {
            return;
        }
        /**
         *  supplier: string
            variable: string
            frequency: string
            stationID: string
            latitude: string
            longitude: string
            hasData: boolean
            utcOffset: number
            timeSequence: string[]
            values: string[]
         */
        const {
            supplier,
            variable,
            frequency,
            stationID,
            latitude,
            longitude,
            country,
            placeName,
        } = stationChartData[0];
        const data: IWeatherChardDataReqModel = {
            options: requestOptions,
            stations: [{
                supplier,
                variable,
                frequency,
                stationID,
                latitude,
                longitude,
                country,
                placeName,
            }]
        };
        try {
            await downloadWithStation(data, 'XLSX', `${stationId}.xlsx`);
        } catch (err) {
            console.log('err');
        }
    };


    return (
        <div
            onMouseEnter={() => {
                setCurrentZIndex(200);
            }}
            onMouseLeave={() => {
                setCurrentZIndex(100);
            }}
            id={`visible-div-marker-${stationId}-inner`}
            style={{ zIndex: `${currentZIndex}` }}
        >
            <svg onClick={(e) => {
                hoverCallBack(true);
                e.stopPropagation();
            }} className='marker-svgIcon' width="42" height="52" viewBox="0 0 42 52" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" clipRule="evenodd" d="M15.0765 41.153C6.36274 38.5959 0 30.541 0 21C0 9.40202 9.40202 0 21 0C32.598 0 42 9.40202 42 21C42 30.541 35.6373 38.5959 26.9235 41.153L21.8944 51.2111C21.5259 51.9482 20.4741 51.9482 20.1056 51.2111L15.0765 41.153Z" fill="white" />
                <path fillRule="evenodd" clipRule="evenodd" d="M21 39C30.9411 39 39 30.9411 39 21C39 11.0589 30.9411 3 21 3C11.0589 3 3 11.0589 3 21C3 30.9411 11.0589 39 21 39Z" fill={color ? color : '#EE5B3A'} />
                <path d="M26.4831 20.289L25.7651 18.589C26.6781 18.203 27.2681 17.311 27.2681 16.319C27.2681 14.961 26.1641 13.857 24.8061 13.857C23.5971 13.857 22.5521 14.756 22.3691 15.947C22.3501 16.069 22.3431 16.195 22.3431 16.319H20.4961C20.4961 16.104 20.5121 15.884 20.5451 15.672C20.8611 13.583 22.6911 12.01 24.8061 12.01C27.1811 12.01 29.1161 13.943 29.1161 16.319C29.1161 18.058 28.0821 19.615 26.4831 20.289Z" fill="white" />
                <path d="M22.1886 14.0715L18.7432 12.717L18.0677 14.4351L21.5132 15.7896L22.1886 14.0715Z" fill="white" />
                <path d="M24.362 12.8489L22.7471 9.51562L21.085 10.3209L22.6999 13.6541L24.362 12.8489Z" fill="white" />
                <path d="M27.8572 10.0867L26.0962 9.52313L25.0233 12.8756L26.7843 13.4392L27.8572 10.0867Z" fill="white" />
                <path d="M31.369 13.6151L30.4336 12.0236L27.2412 13.8999L28.1766 15.4914L31.369 13.6151Z" fill="white" />
                <path d="M31.808 16.9377L28.1895 16.1516L27.7975 17.9555L31.4161 18.7416L31.808 16.9377Z" fill="white" />
                <path d="M26.236 30.471H14.554C12.112 30.471 10.124 28.485 10.124 26.042C10.124 23.898 11.655 22.105 13.682 21.699C14.096 18.365 16.947 15.771 20.395 15.771C22.882 15.771 25.143 17.128 26.323 19.277C29.369 19.324 31.835 21.816 31.835 24.874C31.834 27.96 29.322 30.471 26.236 30.471ZM14.554 23.461C13.13 23.461 11.971 24.618 11.971 26.042C11.971 27.467 13.13 28.625 14.554 28.625H26.237C28.305 28.625 29.988 26.943 29.988 24.875C29.988 22.808 28.305 21.125 26.237 21.125C26.192 21.125 26.124 21.135 26.049 21.145C25.987 21.154 25.924 21.163 25.861 21.168L25.188 21.233L24.923 20.609C24.146 18.792 22.368 17.618 20.397 17.618C17.685 17.618 15.479 19.826 15.479 22.537V23.461H14.554Z" fill="white" />
                <path fillRule="evenodd" clipRule="evenodd" d="M14.554 23.461C13.13 23.461 11.971 24.618 11.971 26.042C11.971 27.467 13.13 28.625 14.554 28.625H26.237C28.305 28.625 29.988 26.943 29.988 24.875C29.988 22.808 28.305 21.125 26.237 21.125C26.1974 21.125 26.1401 21.1327 26.0758 21.1414C26.067 21.1426 26.0581 21.1438 26.049 21.145C25.987 21.154 25.924 21.163 25.861 21.168L25.188 21.233L24.923 20.609C24.146 18.792 22.368 17.618 20.397 17.618C17.685 17.618 15.479 19.826 15.479 22.537V23.461H14.554ZM26.323 19.277C25.143 17.128 22.882 15.771 20.395 15.771C16.947 15.771 14.096 18.365 13.682 21.699C11.655 22.105 10.124 23.898 10.124 26.042C10.124 28.485 12.112 30.471 14.554 30.471H26.236C29.322 30.471 31.834 27.96 31.835 24.874C31.835 21.816 29.369 19.324 26.323 19.277Z" fill="white" />
            </svg>
            {chartVisible && (
                <div style={{ top: `${yPoint}`, right: `${xPoint}`, zIndex: 1000, overflow: 'visible' }} className='weather-chart-div-container'>
                    <div className='chart-detail-div'>
                        <div className='station-info-div'>
                            <div>
                                <span>{stationId}</span><span>({parseFloat(lat).toFixed(4)}</span><span>,{parseFloat(lng).toFixed(4)})</span>
                            </div>
                            <div className='button-div'>
                                <button onClick={() => hoverCallBack(false)}><WeatherStationCloseIcon /></button>
                                <button onClick={() => downloadStationData()}><DownloadXls /></button>
                                {/* <button onClick={() => downloadCurrentChart()}><WeatherDownloadIcon /></button> */}
                            </div>
                        </div>
                        {/* <Tabs className='tab-div' items={items} onChange={(localCurrenTab) => {
                            setCurrenTab(localCurrenTab);
                        }} /> */}
                        <Dropdown
                            placement='bottomLeft'
                            overlay={<Menu items={items} />}
                            trigger={['click']}
                        >
                            <a
                                onClick={e => e.preventDefault()}
                                style={{ marginLeft: '20px' }}
                            >
                                {currenTab}
                                <WeatherArrowDropDown />
                            </a>
                        </Dropdown>
                        <div className='chart-div'>
                            {
                                render && (
                                    <ReactEcharts
                                        option={chartVisibleData || constructNullChartOptionData}
                                        style={{ width: '98%', height: '200px', overflow: 'visible', zIndex: 1000 }}
                                        lazyUpdate={true}
                                        onChartReady={(chartInstance) => defineTheChartInstance(chartInstance)}
                                        notMerge={true}
                                    />
                                )
                            }
                            {
                                !chartVisibleData && (
                                    <span className='no-data-sapn'>No data</span>
                                )
                            }
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WeatherSunnyNormalSvg;